import { FormItem, FormItemProps } from 'components/Form/FormItem';
import { Select } from 'components/Select/Select';
import { Form } from 'antd';
import { isNil } from 'rambda';
import { Input } from 'components/Input/Input';
import { FormattedMessage } from 'react-intl';
import { InputNumber } from 'components/Input/InputNumber';
import { Phase } from '@enmon/commons';
import { ElectricityMeterDetailField } from '../constants';
import { MeterDetails as messages } from '../../messages';
import { IsDirectSelect } from '../../../../Select/meter_details/electricity/IsDirect';
import { MeasuringCurrentTransformerSelect } from '../../../../Select/meter_details/electricity/MeasuringCurrentTransformer';
import { PhasesSelect } from '../../../../Select/meter_details/electricity/Phases';
import { MountingSelect } from '../../../../Select/meter_details/electricity/Mounting';
import { MaxCurrentSelect } from '../../../../Select/meter_details/electricity/MaxCurrent';
import { CircuitBreakerSizeSelect } from '../../../../Select/meter_details/electricity/CircuitBreakerSize';
import { ElectricityMeasurementCategorySelect } from '../../../../Select/meter_details/electricity/ElectricityMeasurementCategory';
import { ElectricityMeterMeasurementTypeSelect } from '../../../../Select/meter_details/electricity/ElectricityMeterMeasurementType';
import { MinCurrentSelect } from '../../../../Select/meter_details/electricity/MinCurrent';
import { RefCurrentSelect } from '../../../../Select/meter_details/electricity/RefCurrent';
import { TariffsSelect } from '../../../../Select/meter_details/electricity/Tariffs';
import { ElectricityDeviceCategorySelect } from '../../../../Select/meter_details/electricity/ElectricityDeviceCategory';

export interface AdditionalElectricityDetails {
  phases?: Phase | null;
  widthUnitProps?: FormItemProps;
}

export const getElectricityDetailFormItem = (
  field: ElectricityMeterDetailField,
  formItemProps: FormItemProps,
  { phases, widthUnitProps }: AdditionalElectricityDetails,
) => {
  switch (field) {
    case ElectricityMeterDetailField.DeviceCategory:
      return (
        <FormItem
          label={<FormattedMessage {...messages.deviceType} />}
          name={['detail', 'deviceCategory']}
          {...formItemProps}
        >
          <ElectricityDeviceCategorySelect />
        </FormItem>
      );
    case ElectricityMeterDetailField.MeasurementType:
      return (
        <FormItem
          label={<FormattedMessage {...messages.meterTechnology} />}
          name={['detail', 'measurementType']}
          tooltip={<FormattedMessage {...messages.electricityMeterMeasurementTypeTooltip} />}
          {...formItemProps}
        >
          <ElectricityMeterMeasurementTypeSelect />
        </FormItem>
      );
    case ElectricityMeterDetailField.MeasurementCategory:
      return (
        <FormItem
          label={<FormattedMessage {...messages.measurementCategory} />}
          name={['detail', 'measurementCategory']}
          tooltip={<FormattedMessage {...messages.electricityMeasurementCategoryTooltip} />}
          {...formItemProps}
        >
          <ElectricityMeasurementCategorySelect />
        </FormItem>
      );
    case ElectricityMeterDetailField.Direct:
      return (
        <FormItem
          label={<FormattedMessage {...messages.connectionType} />}
          name={['detail', 'isDirect']}
          tooltip={<FormattedMessage {...messages.measuringTypeTooltip} />}
          {...formItemProps}
        >
          <IsDirectSelect />
        </FormItem>
      );
    case ElectricityMeterDetailField.MCT:
      return (
        <FormItem
          label={<FormattedMessage {...messages.mct} />}
          name={['detail', 'measuringCurrentTransformer']}
          tooltip={<FormattedMessage {...messages.mctTooltip} />}
          {...formItemProps}
        >
          <MeasuringCurrentTransformerSelect />
        </FormItem>
      );
    case ElectricityMeterDetailField.Phases:
      return (
        <FormItem
          label={<FormattedMessage {...messages.numberOfPhases} />}
          name={['detail', 'phases']}
          tooltip={<FormattedMessage {...messages.numberOfPhasesTooltip} />}
          {...formItemProps}
        >
          <PhasesSelect />
        </FormItem>
      );
    case ElectricityMeterDetailField.Mounting:
      return (
        <FormItem
          label={<FormattedMessage {...messages.mounting} />}
          name={['detail', 'mounting']}
          tooltip={<FormattedMessage {...messages.mountingTooltip} />}
          {...formItemProps}
        >
          <MountingSelect />
        </FormItem>
      );
    case ElectricityMeterDetailField.Width:
      return (
        <FormItem
          label={<FormattedMessage {...messages.width} />}
          name={['detail', 'width', 'value']}
          tooltip={<FormattedMessage {...messages.widthTooltip} />}
          {...formItemProps}
        >
          <InputNumber
            addonAfter={
              <Form.Item noStyle name={['detail', 'width', 'unit']} {...widthUnitProps}>
                <Select
                  options={[
                    { label: 'mm', value: 'mm' },
                    { label: 'DIN', value: 'DIN' },
                  ]}
                />
              </Form.Item>
            }
            className="w-100"
            placeholder=""
          />
        </FormItem>
      );
    case ElectricityMeterDetailField.Tariffs:
      return (
        <FormItem
          label={<FormattedMessage {...messages.tariffs} />}
          name={['detail', 'tariffs']}
          tooltip={<FormattedMessage {...messages.tariffsTooltip} />}
          {...formItemProps}
        >
          <TariffsSelect />
        </FormItem>
      );
    case ElectricityMeterDetailField.IMin:
      return (
        <FormItem
          label={<FormattedMessage {...messages.minimumCurrent} />}
          name={['detail', 'iMin']}
          tooltip={<FormattedMessage {...messages.minimumCurrentTooltip} />}
          {...formItemProps}
        >
          <MinCurrentSelect />
        </FormItem>
      );
    case ElectricityMeterDetailField.IRef:
      return (
        <FormItem
          label={<FormattedMessage {...messages.referenceCurrent} />}
          name={['detail', 'iRef']}
          tooltip={<FormattedMessage {...messages.referenceCurrentTooltip} />}
          {...formItemProps}
        >
          <RefCurrentSelect />
        </FormItem>
      );
    case ElectricityMeterDetailField.IMax:
      return (
        <FormItem
          label={<FormattedMessage {...messages.maximumCurrent} />}
          name={['detail', 'iMax']}
          tooltip={<FormattedMessage {...messages.maximumCurrentTooltip} />}
          {...formItemProps}
        >
          <MaxCurrentSelect />
        </FormItem>
      );
    case ElectricityMeterDetailField.CircuitBreakerSize:
      return (
        <FormItem
          label={<FormattedMessage {...messages.reservedPower} />}
          name={['detail', 'circuitBreakerSize']}
          tooltip={<FormattedMessage {...messages.reservedPowerTooltip} values={{ hasPhases: !isNil(phases) }} />}
          {...formItemProps}
        >
          <CircuitBreakerSizeSelect phases={phases} />
        </FormItem>
      );
    case ElectricityMeterDetailField.InputSpec:
      return (
        <FormItem
          label={<FormattedMessage {...messages.inputSpecification} />}
          name={['detail', 'inputSpec']}
          tooltip={<FormattedMessage {...messages.inputSpecificationTooltip} />}
          {...formItemProps}
        >
          <Input className="w-100" placeholder="" />
        </FormItem>
      );
    default:
      return null;
  }
};
